import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import SubmitButton, { SubmitButtonState } from 'components/Widget/Button/SubmitButton';
import Pill from 'components/Widget/Pill';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import { Loader } from 'components/Widget/Loader';
import * as api from 'dragonmouse-shared/api/forge';
import { DomElement, Frame, FrameStatus, PartialFrame } from 'dragonmouse-shared/interfaces';
import { interceptIfNoCredits } from 'economy';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectUser } from 'redux/db/reducers';
import { setSelectedDomElements } from 'redux/mode/actions';
import { executeCommand } from 'redux/mode/commandManager';
import { MemorializeFrameUpdateCommand } from 'redux/mode/commands';
import { canEditFrame } from 'redux/db/util';
import { displayViewOnlyModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { getElementName } from 'utils';
import * as S from './styles';

interface EditInputProps {
	frame: Frame;
	prototypeWidth?: number;
}

const EditInput = ({ frame, prototypeWidth = 0 }: EditInputProps) => {
	const dispatch = useDispatch();
	const [prompt, setPrompt] = useState<string>('');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	const { selectedDomElements, user } = useSelector(
		(r: RootReducer) => ({
			selectedDomElements: r.mode.selectedDomElements,
			user: selectUser(r.db),
		}),
		shallowEqual
	);
	const numberOfEdits = user?.metadata.numberOfEdits || 0;

	// Responsive suggestion logic
	const showResponsiveSuggestion = prototypeWidth && Math.abs(prototypeWidth - frame.width) > 100;

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	useEffect(() => {
		// Focus the input when elements are selected
		if (selectedDomElements.length > 0 && inputRef.current) {
			inputRef.current.focus();
		}
	}, [selectedDomElements]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onSubmit = async (customPrompt?: string) => {
		if (!canEditFrame(frame)) {
			displayViewOnlyModal();
			return;
		}

		// Check new credit system first
		if (interceptIfNoCredits({ kind: 'chatEdit', sceneId: frame.sceneId })) {
			return;
		}

		setIsSubmitting(true);

		let editResponse: api.EditResponse | undefined = undefined;
		try {
			// Update the existing frame's status
			await forge.updateFrame({
				update: {
					id: frame.id,
					metadata: {
						...frame.metadata,
						status: FrameStatus.PROCESSING,
						statusMessage: customPrompt ? 'Making responsive...' : 'initializing edit...',
					},
				},
			});

			const req: api.EditRequest = {
				sourceFrameId: frame.id,
				targetFrameId: frame.id,
				prompt: customPrompt || prompt,
				selectedDomElements: customPrompt ? [] : selectedDomElements,
			};

			editResponse = await forge.edit(req);
			forge.updateUser({
				update: {
					id: user?.id,
					metadata: {
						numberOfEdits: numberOfEdits + 1,
						lastEditDate: new Date().toISOString(),
					},
				},
			});
		} catch (e: any) {
			if (e.status === 400) {
				const errorMessage = e.response?.data?.error || e.message;

				await forge.updateFrame({
					update: {
						id: frame.id,
						metadata: {
							...frame.metadata,
							status: FrameStatus.ERROR,
							statusMessage: errorMessage,
						},
					},
				});
			} else {
				// Show toast for non-400 errors
				const errorMessage =
					e.response?.data?.error || e.response?.data?.message || e.message || 'Edit failed';
				toast.error(errorMessage);
			}
		} finally {
			if (!customPrompt) {
				setPrompt('');
				dispatch(setSelectedDomElements([]));
			}
			setIsSubmitting(false);
		}

		if (editResponse?.newSiteId) {
			const before: PartialFrame = {
				id: frame.id,
				siteId: frame.siteId,
			};
			const after: PartialFrame = {
				id: frame.id,
				siteId: editResponse.newSiteId,
			};
			executeCommand(new MemorializeFrameUpdateCommand([before], [after]));
		}
	};

	const handleMakeResponsive = () => {
		const responsivePrompt = `make this responsive so it looks good, comfortably filling all screen widths`;
		onSubmit(responsivePrompt);
	};

	const onChangePrompt = (e: ChangeEvent<HTMLTextAreaElement>) => {
		setPrompt(e.target.value);
	};

	const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSubmit();
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderActions = () => {
		const hasInput = prompt.trim().length > 0;
		return (
			<S.Actions>
				<S.Spacer />
				{isSubmitting ? (
					<S.LoadingWrapper>
						<Loader width={20} height={20} />
					</S.LoadingWrapper>
				) : (
					<S.SubmitButtonWrapper>
						<SubmitButton
							state={
								hasInput && !isSubmitting ? SubmitButtonState.REGULAR : SubmitButtonState.DISABLED
							}
							type={IconType.LINE_ARROW_UP}
							tooltip={'Submit edit'}
							tooltipPlacement="bottom-start"
							onClick={onSubmit}
						/>
					</S.SubmitButtonWrapper>
				)}
			</S.Actions>
		);
	};

	return (
		<S.Base>
			{showResponsiveSuggestion && (
				<S.ResponsiveSuggestion>
					<S.SuggestionText>
						Layout not looking good at this width?{' '}
						<S.SuggestionLink onClick={handleMakeResponsive} disabled={isSubmitting}>
							{isSubmitting ? 'Making responsive...' : 'make it responsive'}
						</S.SuggestionLink>
					</S.SuggestionText>
				</S.ResponsiveSuggestion>
			)}
			<S.InputArea isFocused={isFocused}>
				{selectedDomElements.length > 0 && (
					<S.SelectedElements>
						{selectedDomElements.map((element: DomElement) => (
							<Pill key={element.id} label={getElementName(element)} />
						))}
					</S.SelectedElements>
				)}
				<S.MultilineInputWrapper>
					<MultilineInput
						value={prompt}
						onInput={onChangePrompt}
						onKeyDown={onKeyDown}
						placeholder="What would you like to change?"
						minLines={2}
						renderBorder={false}
						onFocusChange={(isFocused) => {
							setIsFocused(isFocused);
						}}
						ref={inputRef}
					/>
				</S.MultilineInputWrapper>
				{renderActions()}
			</S.InputArea>
		</S.Base>
	);
};

export default EditInput;
